/** Feature 2 — structured assistant write commands (preview → confirm → execute). */

import { format } from "date-fns";
import axios from "axios";
import api from "services/api";
import { fetchTeacherDetail } from "services/teacherDetailService";
import { apiurl } from "utils";
import { parseListPayload } from "utils/paginationStats";
import { resolveTeacherQuery, mapTeacherRow } from "assistant/apiHandlers";
import { parseRegistrationHint, normalizeReg, normalizeTeacherText, isTeacherNameFollowUp, isTeacherLookupQuery, isTeacherSearchWithoutName } from "assistant/teacherQuery";
import { resolveTeacherDatabaseId } from "utils/teacherFlags";
import {
  previewAssistantCommand,
  executeAssistantCommand,
  fetchAssistantCommandHistory,
} from "services/assistantCommandsService";
import { fetchTeacherLicenseList } from "services/teacherLicenseService";

const MOBILE_API_ROOT = "https://api.everifpro.com/api/mobile";
/** Same key as Establishments page — mobile list requires X-ApiKey. */
const ESTABLISHMENT_API_KEY =
  process.env.REACT_APP_ESTABLISHMENT_API_KEY || "dztydmpy986xwKgCxHQHMjx1qHRrzMQN";
const ESTABLISHMENTS_FETCH_PAGE_SIZE = 100000;
const withSlash = (url = "") => (url.endsWith("/") ? url : `${url}/`);
const leaveApiBase = withSlash(process.env.REACT_APP_LEAVE_API_BASE || apiurl);

/** Same geo cascade URLs as Teacher License (raw axios + Bearer — not the api instance). */
const GEO_API = {
  provinces: `${apiurl}Assignment/provinces`,
  departments: (provid) => `${apiurl}Assignment/departments?provid=${provid}`,
  subprefectures: (dptid) => `${apiurl}Assignment/subprefectures?dptid=${dptid}`,
  establishments: (sptid) => `${apiurl}Assignment/establishments?sptid=${sptid}`,
};

function authHeaders() {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function establishmentListHeaders() {
  return {
    Accept: "*/*",
    "X-ApiKey": ESTABLISHMENT_API_KEY,
    ...authHeaders(),
  };
}

function normalize(text) {
  return normalizeTeacherText(text).replace(/\u00a0/g, " ").replace(/\s+/g, " ").trim();
}

function todayIso() {
  return format(new Date(), "yyyy-MM-dd");
}

/** Prefer matricule when the user said "… registration 600700". */
function cleanTeacherQuery(raw) {
  const text = String(raw ?? "").trim().replace(/[?.!]+$/, "");
  const reg = parseRegistrationHint(text);
  if (reg) return reg;
  const onlyDigits = text.match(/\b(\d{3,}[A-Za-z0-9-]*)\b/);
  if (onlyDigits && /registration|matricule|reg\b/i.test(text)) return onlyDigits[1];
  return text
    .replace(/\bwith\s+(?:registration|reg|matricule)\s*[:#-]?\s*\d+\w*\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanEstablishmentQuery(raw) {
  const text = String(raw ?? "")
    .trim()
    .replace(/[?.!]+$/, "");
  const stripped = text.replace(/^(?:school|ecole|école|etablissement|établissement)\s+/i, "").trim();
  // Keep "Ecole B" intact when stripping would leave a tiny stub ("B")
  if (stripped.length >= 3) return stripped;
  return text;
}

function establishmentMatchScore(row, query) {
  const q = normalize(query);
  if (!q) return 0;
  const name = normalize(row.name);
  const code = normalize(row.code);
  const hay = `${name} ${code} ${normalize(row.detail)}`.trim();
  if (!hay) return 0;
  if (name === q || code === q) return 100;
  if (name.endsWith(` ${q}`) || name.startsWith(`${q} `)) return 90;
  if (name.includes(q) || (code && code.includes(q))) return 80;
  const tokens = q.split(/\s+/).filter((t) => t.length >= 2);
  if (tokens.length && tokens.every((t) => hay.includes(t))) {
    return 50 + Math.min(20, tokens.length * 5);
  }
  // Single distinctive token (e.g. "bodoro")
  if (q.length >= 4 && hay.includes(q)) return 70;
  return 0;
}

/** Detect if message looks like a write command (strict keywords). */
export function looksLikeWriteCommand(input) {
  const q = normalize(input);
  return (
    /\b(unassign|deassign|desaffecter|retirer)\b/.test(q) ||
    (/\b(remove|end|terminer|stop)\b/.test(q) && /\b(assignment|affectation)\b/.test(q)) ||
    /\b(assign(?:er)?|affecter|reassign|transfer|deplacer|déplacer|mettre|put|move|send)\b/.test(q) ||
    (/\b(create|request|apply|appliquer|creer|créer|demander|soumettre|submit|book|poser)\b/.test(q) &&
      /\b(leave|conge|conges|congé|absence)\b/.test(q)) ||
    (/\b(approve|approuver|reject|rejeter|refuser|accepter)\b/.test(q) &&
      /\b(leave|conge|conges|absence)\b/.test(q)) ||
    (/\b(activate|activer|deactivate|desactiver|enable|disable|grant|revoke|turn\s+on|turn\s+off|give|remove)\b/.test(
      q
    ) &&
      /\b(license|licence|licenci)\b/.test(q)) ||
    /\b(command history|historique (des )?commandes|assistant history)\b/.test(q)
  );
}

/**
 * Softer signal that the user may want a write action — used to trigger LLM classify
 * when rigid regex parsing misses natural phrasing.
 */
export function smellsLikeWriteIntent(input) {
  const q = normalize(input);
  if (!q || q.length < 4) return false;
  if (isTeacherNameFollowUp(input)) return false;
  if (isTeacherLookupQuery(input) && !isTeacherSearchWithoutName(input)) return false;
  if (looksLikeWriteCommand(q)) return true;
  const actionish =
    /\b(license|licence|licenci|assign|affect|unassign|leave|conge|absence|school|ecole|etablissement|matricule|registration)\b/.test(
      q
    );
  const hasTeacherHint =
    /\b\d{3,}[a-z0-9-]*\b/.test(q) ||
    /\b(teacher|enseignant)\b/.test(q) ||
    (/\b(leave|conge|cong[eé]|absence)\b/.test(q) && /\b(for|pour|de|of)\b/.test(q));
  return actionish && (hasTeacherHint || /\b(from|to|a|à|de|du|au)\b/.test(q));
}

/**
 * Parse NL into a draft command (IDs may still be unresolved).
 */
export function parseWriteCommand(input) {
  const raw = String(input ?? "").trim();
  const q = normalize(raw);
  if (!q) return null;

  if (/\b(command history|historique (des )?commandes|assistant history)\b/.test(q)) {
    return { kind: "history" };
  }

  let m;

  // License first when the message mentions license (avoid "remove license from X" → unassign)
  if (/\b(license|licence|licenci)/i.test(raw)) {
    m =
      raw.match(
        /\b(activate|activer|deactivate|desactiver|désactiver|enable|disable|grant|revoke)\s+(?:the\s+)?(?:license|licence|licenci(?:e|é)?)\s+(?:for|de|pour|of|to)\s+(.+)$/i
      ) ||
      raw.match(
        /\b(activate|activer|deactivate|desactiver|désactiver|enable|disable|grant|revoke)\s+(?:license|licence)\s+(?:for\s+|de\s+|pour\s+|of\s+|to\s+)?(.+)$/i
      ) ||
      raw.match(/\b(?:turn\s+on|give)\s+(?:the\s+)?(?:license|licence)\s+(?:for|to|de|pour|of)\s+(.+)$/i) ||
      raw.match(
        /\b(?:turn\s+off|take\s+away|remove|revoke)\s+(?:the\s+)?(?:license|licence)\s+(?:for|from|de|pour|of)\s+(.+)$/i
      );
    if (m) {
      const head = normalize(m[0]);
      const verb = normalize(m[1] || "");
      const deactivate =
        verb.startsWith("deactiv") ||
        verb.startsWith("desactiv") ||
        verb === "disable" ||
        verb === "revoke" ||
        /\bturn\s+off\b|\btake\s+away\b|\bremove\b|\brevoke\b/.test(head);
      const teacherPart = m[2] || m[1];
      return {
        kind: "command",
        command: deactivate ? "deactivate_license" : "activate_license",
        hints: { teacherQuery: cleanTeacherQuery(teacherPart) },
      };
    }
  }

  // Unassign — before assign (avoid confusion with "assign")
  m =
    raw.match(
      /\b(?:unassign|deassign|désaffecter|desaffecter)\s+(?:teacher\s+|enseignant(?:e)?\s+)?(.+?)(?:\s+(?:from|de|à|a)\s+(.+))?$/i
    ) ||
    raw.match(
      /\b(?:retirer|remove|end|terminer|stop)\s+(?:the\s+)?(?:assignment|affectation)\s+(?:for|de|pour|of)\s+(.+?)(?:\s+(?:from|de|at|à|a)\s+(.+))?$/i
    ) ||
    raw.match(/\b(?:retirer)\s+(.+?)\s+(?:de|from|à|a)\s+(.+)$/i) ||
    raw.match(
      /\b(?:remove|take)\s+(.+?)\s+(?:off|from)\s+(?:the\s+)?(?:school|ecole|école|etablissement|établissement)\s*(.*)$/i
    );
  if (m) {
    const estRaw = (m[2] || "").trim();
    return {
      kind: "command",
      command: "unassign_teacher",
      hints: {
        teacherQuery: cleanTeacherQuery(m[1]),
        establishmentQuery: estRaw ? cleanEstablishmentQuery(estRaw) : "",
      },
    };
  }

  // Apply / create leave — shorthand "apply leave for X" → ask for type + dates
  m =
    raw.match(
      /\b(?:apply|appliquer)\s+(?:a\s+|an\s+|un\s+)?(?:sick|medical|maladie)\s+(?:leave|conge|cong[eé]|absence)(?:\s+request)?\s+(?:for|de|pour|of|to)\s+(.+?)\s+(?:from|du|de)\s+(\d{4}-\d{2}-\d{2})\s+(?:to|au|a|à)\s+(\d{4}-\d{2}-\d{2})\s*$/i
    ) ||
    raw.match(
      /\b(?:apply|appliquer)\s+(?:a\s+|an\s+|un\s+)?(?:leave|conge|cong[eé]|absence)(?:\s+request)?\s+(?:for|de|pour|of|to)\s+(.+?)\s+(?:from|du|de)\s+(\d{4}-\d{2}-\d{2})\s+(?:to|au|a|à)\s+(\d{4}-\d{2}-\d{2})(?:\s+(.+))?\s*$/i
    );
  if (m) {
    const typeHint =
      /\b(sick|medical|maladie)\b/i.test(raw) ? "sick" : String(m[4] || m[0] || "sick").trim();
    return {
      kind: "command",
      command: "create_leave_request",
      hints: {
        teacherQuery: cleanTeacherQuery(m[1]),
        startDate: m[2],
        endDate: m[3],
        leaveTypeQuery: typeHint,
        reason: typeHint || "Requested via assistant",
        status: "Pending",
      },
    };
  }

  m =
    raw.match(
      /\b(?:apply|appliquer)\s+(?:a\s+|an\s+|un\s+)?(?:leave|conge|cong[eé]|absence)(?:\s+request)?\s+(?:for|de|pour|of|to)\s+(.+?)\s*$/i
    ) ||
    raw.match(
      /\b(?:create|request|creer|créer|demander|soumettre|submit|book|poser)\s+(?:a\s+|an\s+|un\s+)?(?:leave|conge|cong[eé]|absence)(?:\s+request)?\s+(?:for|de|pour|of)\s+(.+?)\s*$/i
    );
  if (m && !/\d{4}-\d{2}-\d{2}/.test(raw)) {
    return {
      kind: "await_leave_details",
      command: "create_leave_request",
      hints: { teacherQuery: cleanTeacherQuery(m[1]) },
    };
  }

  // Create leave request — with explicit dates
  m =
    raw.match(
      /\b(?:create|request|creer|créer|demander|soumettre|submit|book|poser)\s+(?:a\s+|an\s+|un\s+)?(?:leave|conge|cong[eé]|absence)(?:\s+request)?\s+(?:for|de|pour|of)\s+(.+?)\s+(?:from|du|de)\s+(\d{4}-\d{2}-\d{2})\s+(?:to|au|a|à)\s+(\d{4}-\d{2}-\d{2})(?:\s+(?:type|reason|raison)\s*[:\s-]*)?(.+)?$/i
    ) ||
    raw.match(
      /\b(?:create|request|creer|créer|demander|soumettre|submit|book|poser)\s+(?:a\s+|an\s+|un\s+)?(?:leave|conge|cong[eé]|absence)(?:\s+request)?\s+(?:for|de|pour|of)\s+(.+?)\s+(\d{4}-\d{2}-\d{2})\s*(?:to|au|->|–|-)\s*(\d{4}-\d{2}-\d{2})(?:\s+(.+))?$/i
    ) ||
    raw.match(
      /\b(.+?)\s+(?:needs|wants|demande|demande un)\s+(?:leave|conge|cong[eé]|absence)\s+(?:from|du|de)\s+(\d{4}-\d{2}-\d{2})\s+(?:to|au|a|à)\s+(\d{4}-\d{2}-\d{2})(?:\s+(.+))?$/i
    );
  if (m) {
    const trailing = String(m[4] || "")
      .trim()
      .replace(/^(?:type|reason|raison)\s*[:\s-]*/i, "")
      .trim();
    return {
      kind: "command",
      command: "create_leave_request",
      hints: {
        teacherQuery: cleanTeacherQuery(m[1]),
        startDate: m[2],
        endDate: m[3],
        leaveTypeQuery: trailing,
        reason: trailing || "Requested via assistant",
        status: "Pending",
      },
    };
  }

  // Assign — natural variants: put/move/send/transfer … to …
  m =
    raw.match(
      /\b(?:assign(?:er)?|affecter|reassign|transfer(?:er)?|déplacer|deplacer|mettre|put|move|send)\s+(?:teacher\s+|enseignant(?:e)?\s+)?(.+?)\s+(?:to|a|à|at|vers)\s+(.+?)(?:\s+on\s+(\d{4}-\d{2}-\d{2}))?\s*$/i
    ) ||
    raw.match(
      /\b(?:create assignment|creer affectation|créer affectation)\s+(?:for\s+|de\s+|pour\s+)?(.+?)\s+(?:to|a|à|at)\s+(.+?)(?:\s+on\s+(\d{4}-\d{2}-\d{2}))?\s*$/i
    );
  if (m) {
    return {
      kind: "command",
      command: "assign_teacher",
      hints: {
        teacherQuery: cleanTeacherQuery(m[1]),
        establishmentQuery: cleanEstablishmentQuery(m[2]),
        assignmentDate: m[3] || todayIso(),
      },
    };
  }

  // approve / reject leave
  m =
    raw.match(
      /\b(approve|approuver|reject|rejeter|refuser|accepter)\s+(?:the\s+)?(?:leave|conge|cong[eé]|absence)\s*(?:request\s*)?(?:for|de|pour|of)?\s*(.+)?$/i
    ) ||
    raw.match(
      /\b(approve|approuver|reject|rejeter|refuser|accepter)\s+(.+?)(?:'s)?\s+(?:leave|conge|cong[eé]|absence)\b/i
    );
  if (m) {
    const verb = normalize(m[1]);
    const status =
      verb.startsWith("approuv") || verb === "approve" || verb.startsWith("accept")
        ? "Approved"
        : "Rejected";
    return {
      kind: "command",
      command: "update_leave_status",
      hints: { teacherQuery: cleanTeacherQuery(m[2] || ""), status },
    };
  }

  return null;
}

/** Complete a pending leave draft from the user's follow-up (type + dates). */
export function parseLeaveDetailsReply(input, pendingDraft) {
  const raw = String(input ?? "").trim();
  if (!raw || !pendingDraft?.hints?.teacherQuery) return null;

  const dates = raw.match(/(\d{4}-\d{2}-\d{2})\s*(?:to|au|a|à|→|–|-+)\s*(\d{4}-\d{2}-\d{2})/i);
  if (!dates) return null;

  let leaveTypeQuery = "";
  const typeMatch = raw.match(
    /\b(sick|medical|maladie|annual|annuel|maternity|maternite|maternité|personal|personnel|cong[eé]\s+maladie|sick\s+leave)\b/i
  );
  if (typeMatch) {
    leaveTypeQuery = typeMatch[1];
  } else {
    const beforeDates = raw.split(dates[1])[0].replace(/\b(from|du|de|leave|conge|congé|for|pour)\b/gi, "").trim();
    if (beforeDates.length >= 3) leaveTypeQuery = beforeDates;
  }

  if (!leaveTypeQuery) leaveTypeQuery = "sick";

  return {
    kind: "command",
    command: "create_leave_request",
    hints: {
      teacherQuery: pendingDraft.hints.teacherQuery,
      startDate: dates[1],
      endDate: dates[2],
      leaveTypeQuery,
      reason: leaveTypeQuery,
      status: "Pending",
    },
  };
}

/** Normalize a draft coming from LLM classify into the same shape as parseWriteCommand. */
export function draftFromClassifiedIntent(classified) {
  if (!classified || classified.kind !== "command") return null;
  const allowed = new Set([
    "assign_teacher",
    "unassign_teacher",
    "update_leave_status",
    "create_leave_request",
    "activate_license",
    "deactivate_license",
  ]);
  const command = String(classified.command || "").trim();
  if (!allowed.has(command)) return null;
  const confidence = Number(classified.confidence);
  if (Number.isFinite(confidence) && confidence < 0.55) return null;

  const h = classified.hints || {};
  const hints = {
    teacherQuery: cleanTeacherQuery(h.teacherQuery || h.teacher || ""),
    establishmentQuery: h.establishmentQuery
      ? cleanEstablishmentQuery(h.establishmentQuery)
      : h.establishment
        ? cleanEstablishmentQuery(h.establishment)
        : "",
    assignmentDate: h.assignmentDate || todayIso(),
    startDate: h.startDate || "",
    endDate: h.endDate || "",
    leaveTypeQuery: String(h.leaveTypeQuery || h.leaveType || h.reason || "").trim(),
    reason: String(h.reason || h.leaveTypeQuery || "Requested via assistant").trim(),
    status: h.status || (command === "update_leave_status" ? "Approved" : "Pending"),
  };

  if (
    (command === "assign_teacher" ||
      command === "unassign_teacher" ||
      command === "activate_license" ||
      command === "deactivate_license" ||
      command === "update_leave_status" ||
      command === "create_leave_request") &&
    !hints.teacherQuery
  ) {
    return null;
  }
  if (command === "assign_teacher" && !hints.establishmentQuery) return null;
  if (command === "create_leave_request" && (!hints.startDate || !hints.endDate)) return null;

  return { kind: "command", command, hints };
}

/**
 * Teacher License / Assignment rows: NEVER use row.id / row.name — those are the teacher
 * (or assignment), not the school. Only establishment* / estab* fields.
 */
function mapEstablishmentFromLinkedRow(row) {
  if (!row) return null;
  const id = row.establishmentId ?? row.EstablishmentId ?? row.estabId ?? row.EstabId;
  const name =
    row.establishmentName ??
    row.EstablishmentName ??
    row.estabName ??
    row.EstName ??
    row.establishment ??
    row.Establishment;
  if (id == null || id === "" || !name) return null;
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) return null;
  return {
    id: n,
    name: String(name).trim(),
    code: row.establishmentCode ?? row.code ?? row.Code ?? "",
    detail: [
      row.subPrefectureName ?? row.SubPrefectureName ?? row.spName ?? row.SpName,
      row.provinceName ?? row.ProvinceName,
      row.departmentName ?? row.DepartmentName,
    ]
      .filter(Boolean)
      .join(" · "),
  };
}

/** Directory / Assignment/establishments dropdown rows. */
function mapEstablishmentFromDirectoryRow(row, extra = {}) {
  if (!row) return null;
  const id = row.id ?? row.Id ?? row.estabId ?? row.EstabId ?? row.establishmentId ?? row.EstablishmentId;
  const name =
    row.name ??
    row.Name ??
    row.establishment ??
    row.Establishment ??
    row.establishmentName ??
    row.EstablishmentName ??
    row.estabName ??
    row.EstName;
  if (id == null || id === "" || !name) return null;
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) return null;
  return {
    id: n,
    name: String(name).trim(),
    code: row.code ?? row.Code ?? "",
    detail: [
      extra.subPrefectureName ||
        row.spName ||
        row.SPName ||
        row.SpName ||
        row.subPrefectureName ||
        row.SubPrefectureName,
      extra.provinceName || row.provinceName || row.ProvinceName,
      extra.departmentName || row.departmentName || row.DepartmentName,
    ]
      .filter(Boolean)
      .join(" · "),
  };
}

function rankEstablishmentHits(candidates, query) {
  const byId = new Map();
  for (const e of candidates) {
    if (!e?.id || !e?.name) continue;
    const score = establishmentMatchScore(e, query);
    if (score <= 0) continue;
    const key = String(e.id);
    const prev = byId.get(key);
    if (!prev || score > prev.score) byId.set(key, { e, score });
  }
  return [...byId.values()].sort(
    (a, b) => b.score - a.score || String(a.e.name).localeCompare(String(b.e.name))
  );
}

function finalizeEstablishmentHits(scored, query, limit) {
  if (!scored.length) return { status: "not_found", query };
  if (scored.length === 1 || scored[0].score >= 90) {
    return { status: "found", establishment: scored[0].e, query };
  }
  if (scored[0].score >= 70 && scored[0].score - (scored[1]?.score || 0) >= 20) {
    return { status: "found", establishment: scored[0].e, query };
  }
  return { status: "ambiguous", matches: scored.slice(0, limit).map((x) => x.e), query };
}

async function loadMobileEstablishments() {
  try {
    const { data } = await axios.get(`${MOBILE_API_ROOT}/establishments`, {
      params: { pageNumber: 1, pageSize: ESTABLISHMENTS_FETCH_PAGE_SIZE },
      headers: establishmentListHeaders(),
    });
    const rows = Array.isArray(data) ? data : parseListPayload(data);
    return rows.map((r) => mapEstablishmentFromDirectoryRow(r)).filter(Boolean);
  } catch (err) {
    console.warn("[Assistant] mobile establishment list failed", err?.message || err);
    return [];
  }
}

/**
 * When a Teacher License row shows the school name but omits establishmentId,
 * load Assignment/establishments for that row's sub-prefecture (same as the filter dropdown).
 */
async function resolveEstabViaRowSubprefecture(row, query) {
  const nameHint =
    row.establishmentName ??
    row.EstablishmentName ??
    row.estabName ??
    row.EstName ??
    "";
  if (!nameHint || establishmentMatchScore({ id: 1, name: nameHint, code: "", detail: "" }, query) <= 0) {
    return [];
  }
  const sptid = row.subPrefectureId ?? row.SubPrefectureId ?? row.spId ?? row.SpId ?? row.SPId;
  if (sptid == null || sptid === "") return [];
  try {
    const { data } = await axios.get(GEO_API.establishments(sptid), { headers: authHeaders() });
    return geoList(data)
      .map((erow) =>
        mapEstablishmentFromDirectoryRow(erow, {
          provinceName: row.provinceName ?? row.ProvinceName,
          departmentName: row.departmentName ?? row.DepartmentName,
          subPrefectureName: row.subPrefectureName ?? row.SubPrefectureName,
        })
      )
      .filter((e) => e && establishmentMatchScore(e, query) > 0);
  } catch (err) {
    console.warn("[Assistant] estab via subprefecture failed", sptid, err?.message || err);
    return [];
  }
}

/** Fast path — schools already on Teacher License / Assignment screens (e.g. AWINE GASSAR). */
async function loadEstablishmentsFromPortalLists(query) {
  const out = [];
  const spTried = new Set();
  try {
    const license = await fetchTeacherLicenseList({ pageNumber: 1, pageSize: 500 });
    const rows = parseListPayload(license);
    for (const row of rows) {
      const mapped = mapEstablishmentFromLinkedRow(row);
      if (mapped) {
        out.push(mapped);
        continue;
      }
      // Name visible on license table but id missing → resolve via that SP's establishment list
      const sptid = row.subPrefectureId ?? row.SubPrefectureId ?? row.spId ?? row.SpId;
      const spKey = sptid != null ? String(sptid) : "";
      if (spKey && spTried.has(spKey)) continue;
      if (spKey) spTried.add(spKey);
      const viaSp = await resolveEstabViaRowSubprefecture(row, query);
      out.push(...viaSp);
    }
    console.info(
      "[Assistant] estabs from TeacherLicense",
      out.length,
      "sample",
      out.slice(0, 5).map((e) => `${e.name}#${e.id}`)
    );
  } catch (err) {
    console.warn("[Assistant] TeacherLicense estab scan failed", err?.message || err);
  }

  try {
    const { data } = await api.get("/Assignment/summary", {
      params: { pageNumber: 1, pageSize: 500 },
      headers: authHeaders(),
    });
    for (const row of parseListPayload(data)) {
      const mapped = mapEstablishmentFromLinkedRow(row);
      if (mapped) {
        out.push(mapped);
        continue;
      }
      const sptid = row.subPrefectureId ?? row.SubPrefectureId ?? row.spId ?? row.SpId;
      const spKey = sptid != null ? `a:${sptid}` : "";
      if (spKey && spTried.has(spKey)) continue;
      if (spKey) spTried.add(spKey);
      const viaSp = await resolveEstabViaRowSubprefecture(row, query);
      out.push(...viaSp);
    }
  } catch (err) {
    console.warn("[Assistant] Assignment summary estab scan failed", err?.message || err);
  }
  return out;
}

function geoList(data) {
  return Array.isArray(data) ? data : parseListPayload(data);
}

/**
 * Same source as the Teacher License establishment dropdown
 * (raw axios + Assignment/establishments cascade).
 */
async function loadEstablishmentsFromAssignmentGeo(query, { maxMatches = 12 } = {}) {
  const q = normalize(query);
  if (!q) return [];
  const hits = [];
  const seen = new Set();
  const headers = authHeaders();

  try {
    const { data: pData } = await axios.get(GEO_API.provinces, { headers });
    const provinces = geoList(pData);
    console.info("[Assistant] Assignment geo provinces", provinces.length);

    for (const prov of provinces) {
      const provid = prov.provid ?? prov.ProvId ?? prov.id ?? prov.Id;
      if (provid == null) continue;
      let depts = [];
      try {
        const { data } = await axios.get(GEO_API.departments(provid), { headers });
        depts = geoList(data);
      } catch {
        continue;
      }

      for (const dept of depts) {
        const dptid = dept.dptid ?? dept.DptId ?? dept.id ?? dept.Id;
        if (dptid == null) continue;
        let subps = [];
        try {
          const { data } = await axios.get(GEO_API.subprefectures(dptid), { headers });
          subps = geoList(data);
        } catch {
          continue;
        }

        for (const subp of subps) {
          const sptid = subp.sptid ?? subp.SpId ?? subp.SPId ?? subp.id ?? subp.Id;
          if (sptid == null) continue;
          let estabs = [];
          try {
            const { data } = await axios.get(GEO_API.establishments(sptid), { headers });
            estabs = geoList(data);
          } catch {
            continue;
          }

          for (const row of estabs) {
            const mapped = mapEstablishmentFromDirectoryRow(row, {
              provinceName: prov.name ?? prov.Name ?? prov.province,
              departmentName: dept.name ?? dept.Name ?? dept.department,
              subPrefectureName: subp.name ?? subp.Name ?? subp.subprefecture,
            });
            if (!mapped) continue;
            const score = establishmentMatchScore(mapped, query);
            if (score <= 0) continue;
            const key = String(mapped.id);
            if (seen.has(key)) continue;
            seen.add(key);
            hits.push(mapped);
            console.info("[Assistant] geo estab hit", mapped.name, mapped.id, "score", score);
            if (score >= 90 || hits.length >= maxMatches) return hits;
          }
        }
      }
    }
  } catch (err) {
    console.warn("[Assistant] Assignment geo estab search failed", err?.message || err);
  }
  return hits;
}

async function resolveEstablishmentQuery(query, limit = 6) {
  const q = normalize(query);
  if (!q) return { status: "missing" };
  if (q.length < 2) return { status: "not_found", query };

  console.info("[Assistant] resolve establishment", query);

  // 1) Teacher License / Assignment rows first (what the user already sees in filters)
  let scored = rankEstablishmentHits(await loadEstablishmentsFromPortalLists(query), query);
  let result = finalizeEstablishmentHits(scored, query, limit);
  if (result.status !== "not_found") {
    console.info("[Assistant] estab resolved via portal lists", result.status, result.establishment || result.matches);
    return result;
  }

  // 2) Mobile directory
  scored = rankEstablishmentHits(await loadMobileEstablishments(), query);
  result = finalizeEstablishmentHits(scored, query, limit);
  if (result.status !== "not_found") {
    console.info("[Assistant] estab resolved via mobile directory", result);
    return result;
  }

  // 3) Full Assignment geo cascade — same dropdown as Teacher License
  scored = rankEstablishmentHits(await loadEstablishmentsFromAssignmentGeo(query), query);
  result = finalizeEstablishmentHits(scored, query, limit);
  console.info("[Assistant] estab resolve final", result.status, query);
  return result;
}

async function resolveLeaveForTeacher(teacher) {
  const { data } = await api.get(`${leaveApiBase}LeaveRequests/list`, {
    params: { pageNumber: 1, pageSize: 200 },
    headers: authHeaders(),
  });
  const rows = parseListPayload(data);
  const matches = rows.filter((row) => {
    const tid = row.teacherId ?? row.TeacherId;
    const rowStatus = normalize(row.status ?? row.Status);
    if (rowStatus !== "pending") return false;
    if (tid && teacher.id && String(tid) === String(teacher.id)) return true;
    const tName = normalize(row.teacherName ?? row.TeacherName ?? "");
    return tName && (tName.includes(normalize(teacher.name)) || normalize(teacher.name).includes(tName));
  });

  const mapped = matches.map((row) => ({
    id: row.id ?? row.Id,
    teacherName: row.teacherName ?? row.TeacherName ?? teacher.name,
    leaveType: row.leaveType ?? row.LeaveType ?? "—",
    status: row.status ?? row.Status,
    startDate: String(row.startDate ?? row.StartDate ?? "").slice(0, 10),
    endDate: String(row.endDate ?? row.EndDate ?? "").slice(0, 10),
  }));

  if (!mapped.length) return { status: "not_found" };
  if (mapped.length === 1) return { status: "found", leave: mapped[0] };
  return { status: "ambiguous", matches: mapped };
}

/**
 * Resolve a real DB teacher id (never send matricule as teacherId).
 */
async function resolveCanonicalTeacherId(teacher, teacherQuery) {
  const fromRow = resolveTeacherDatabaseId(teacher);
  if (fromRow.id) {
    return { teacherId: fromRow.id, teacher: { ...teacher, id: fromRow.id, reg: fromRow.regNumber || teacher.reg } };
  }

  const reg =
    normalizeReg(teacher?.reg) ||
    normalizeReg(parseRegistrationHint(teacherQuery || "") || "") ||
    "";

  // Detail by id if list id looks real and different from reg
  const listId = Number(teacher?.id);
  if (Number.isFinite(listId) && listId > 0 && String(listId) !== String(reg)) {
    return { teacherId: listId, teacher: { ...teacher, id: listId } };
  }

  // Detail by regNumber to get true PK
  if (reg && reg !== "—") {
    try {
      const detail = await fetchTeacherDetail({ regNumber: reg, historyLimit: 1, chartMonths: 1 });
      const profile = detail?.profile || detail?.Profile || {};
      const detailId = Number(profile.id ?? profile.Id);
      if (Number.isFinite(detailId) && detailId > 0 && String(detailId) !== reg) {
        return {
          teacherId: detailId,
          teacher: {
            ...teacher,
            id: detailId,
            name: profile.fullName || teacher.name,
            reg: profile.regNumber || reg,
          },
        };
      }
      if (Number.isFinite(detailId) && detailId > 0) {
        return { teacherId: detailId, teacher: { ...teacher, id: detailId, reg } };
      }
    } catch (err) {
      console.warn("[Assistant] teacher detail resolve failed", err?.message || err);
    }
  }

  // Last resort: list id even if equal to reg (some tenants use numeric matricule as PK)
  if (Number.isFinite(listId) && listId > 0) {
    return { teacherId: listId, teacher: { ...teacher, id: listId } };
  }

  return { teacherId: null, teacher };
}

async function fetchLeaveTypes() {
  const { data } = await api.get(`${leaveApiBase}LeaveRequests/leavetypes`, {
    headers: authHeaders(),
  });
  const rows = Array.isArray(data?.data) ? data.data : parseListPayload(data);
  return rows
    .map((row) => ({
      id: Number(row.id ?? row.Id),
      name: String(row.name ?? row.Name ?? row.leaveType ?? row.LeaveType ?? "").trim(),
    }))
    .filter((t) => Number.isFinite(t.id) && t.id > 0 && t.name);
}

async function resolveLeaveTypeQuery(query, limit = 8) {
  let types = [];
  try {
    types = await fetchLeaveTypes();
  } catch (err) {
    console.warn("[Assistant] leave types failed", err?.message || err);
    return { status: "not_found", query, error: err?.message };
  }
  if (!types.length) return { status: "not_found", query };

  const q = normalize(query);
  if (!q) {
    if (types.length === 1) return { status: "found", leaveType: types[0] };
    return { status: "ambiguous", matches: types.slice(0, limit), query: "" };
  }

  const scored = types
    .map((t) => {
      const name = normalize(t.name);
      let score = 0;
      if (name === q || String(t.id) === q) score = 100;
      else if (name.includes(q) || q.includes(name)) score = 80;
      else if (q.length >= 3 && name.startsWith(q)) score = 70;
      return { t, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.t.name.localeCompare(b.t.name));

  if (!scored.length) return { status: "not_found", query };
  if (scored.length === 1 || scored[0].score >= 90) {
    return { status: "found", leaveType: scored[0].t };
  }
  if (scored[0].score - (scored[1]?.score || 0) >= 20) {
    return { status: "found", leaveType: scored[0].t };
  }
  return { status: "ambiguous", matches: scored.slice(0, limit).map((x) => x.t), query };
}

/**
 * Build payload after resolving all entities, or return a pick step.
 */
export async function buildCommandPayload(draft) {
  const command = draft.command;
  const next = { ...draft };

  const needsTeacher = [
    "assign_teacher",
    "unassign_teacher",
    "activate_license",
    "deactivate_license",
    "update_leave_status",
    "create_leave_request",
  ].includes(command);

  if (needsTeacher) {
    if (!next.teacherId && next.hints?.teacherQuery) {
      const query = cleanTeacherQuery(next.hints.teacherQuery);
      const resolved = await resolveTeacherQuery(query, 8);
      if (resolved.status === "ambiguous") {
        return { ok: false, reason: "pick_teacher", matches: resolved.matches, draft: next };
      }
      if (resolved.status !== "found") {
        return { ok: false, reason: "teacher_not_found", query, draft: next };
      }
      const canonical = await resolveCanonicalTeacherId(resolved.teacher, query);
      next.teacherId = canonical.teacherId;
      next.teacher = canonical.teacher;
      if (!next.teacherId) {
        return { ok: false, reason: "teacher_not_found", query, draft: next };
      }
    } else if (next.teacherId) {
      const canonical = await resolveCanonicalTeacherId(
        next.teacher || { id: next.teacherId, reg: next.hints?.teacherQuery },
        next.hints?.teacherQuery
      );
      next.teacherId = canonical.teacherId || Number(next.teacherId);
      next.teacher = canonical.teacher || next.teacher;
    }
  }

  if (command === "assign_teacher") {
    if (!next.establishmentId && next.hints?.establishmentQuery) {
      const estQuery = cleanEstablishmentQuery(next.hints.establishmentQuery);
      const resolved = await resolveEstablishmentQuery(estQuery, 8);
      if (resolved.status === "ambiguous") {
        return { ok: false, reason: "pick_establishment", matches: resolved.matches, draft: { ...next, hints: { ...next.hints, establishmentQuery: estQuery } } };
      }
      if (resolved.status !== "found") {
        return {
          ok: false,
          reason: "establishment_not_found",
          query: estQuery,
          draft: next,
        };
      }
      next.establishmentId = Number(resolved.establishment.id);
      next.establishment = resolved.establishment;
    }

    if (!next.teacherId || !next.establishmentId) {
      return {
        ok: false,
        reason: !next.teacherId ? "teacher_not_found" : "establishment_not_found",
        query: !next.teacherId ? next.hints?.teacherQuery : next.hints?.establishmentQuery,
        draft: next,
      };
    }

    const assignmentDate = next.hints?.assignmentDate || next.assignmentDate || todayIso();
    return {
      ok: true,
      command,
      payload: {
        teacherId: Number(next.teacherId),
        establishmentId: Number(next.establishmentId),
        assignmentDate,
      },
      draft: next,
    };
  }

  if (command === "unassign_teacher") {
    if (!next.teacherId) {
      return { ok: false, reason: "teacher_not_found", query: next.hints?.teacherQuery, draft: next };
    }

    // establishmentId optional — resolve only when the user named a school
    if (!next.establishmentId && next.hints?.establishmentQuery) {
      const estQuery = cleanEstablishmentQuery(next.hints.establishmentQuery);
      if (estQuery) {
        const resolved = await resolveEstablishmentQuery(estQuery, 8);
        if (resolved.status === "ambiguous") {
          return {
            ok: false,
            reason: "pick_establishment",
            matches: resolved.matches,
            draft: { ...next, hints: { ...next.hints, establishmentQuery: estQuery } },
          };
        }
        if (resolved.status !== "found") {
          return { ok: false, reason: "establishment_not_found", query: estQuery, draft: next };
        }
        next.establishmentId = Number(resolved.establishment.id);
        next.establishment = resolved.establishment;
      }
    }

    const payload = { teacherId: Number(next.teacherId) };
    if (next.establishmentId) payload.establishmentId = Number(next.establishmentId);
    return { ok: true, command, payload, draft: next };
  }

  if (command === "activate_license" || command === "deactivate_license") {
    if (!next.teacherId) {
      return { ok: false, reason: "teacher_not_found", query: next.hints?.teacherQuery, draft: next };
    }
    return {
      ok: true,
      command,
      payload: { teacherId: Number(next.teacherId) },
      draft: next,
    };
  }

  if (command === "create_leave_request") {
    if (!next.teacherId) {
      return { ok: false, reason: "teacher_not_found", query: next.hints?.teacherQuery, draft: next };
    }
    const startDate = next.hints?.startDate || next.startDate;
    const endDate = next.hints?.endDate || next.endDate;
    if (!startDate || !endDate) {
      return { ok: false, reason: "leave_dates_required", draft: next };
    }

    if (!next.leaveTypeId) {
      const typeRes = await resolveLeaveTypeQuery(next.hints?.leaveTypeQuery || "", 8);
      if (typeRes.status === "ambiguous") {
        return { ok: false, reason: "pick_leave_type", matches: typeRes.matches, draft: next };
      }
      if (typeRes.status !== "found") {
        return {
          ok: false,
          reason: "leave_type_not_found",
          query: next.hints?.leaveTypeQuery || "",
          draft: next,
        };
      }
      next.leaveTypeId = typeRes.leaveType.id;
      next.leaveType = typeRes.leaveType;
    }

    const reason =
      String(next.hints?.reason || next.reason || next.leaveType?.name || "Requested via assistant").trim() ||
      "Requested via assistant";
    const status = next.hints?.status || next.status || "Pending";

    return {
      ok: true,
      command,
      payload: {
        teacherId: Number(next.teacherId),
        leaveTypeId: Number(next.leaveTypeId),
        startDate,
        endDate,
        reason,
        status,
      },
      draft: next,
    };
  }

  if (command === "update_leave_status") {
    if (!next.leaveRequestId) {
      const leaveRes = await resolveLeaveForTeacher(next.teacher || { id: next.teacherId, name: "" });
      if (leaveRes.status === "ambiguous") {
        return { ok: false, reason: "pick_leave", matches: leaveRes.matches, draft: next };
      }
      if (leaveRes.status !== "found") {
        return { ok: false, reason: "leave_not_found", draft: next };
      }
      next.leaveRequestId = leaveRes.leave.id;
      next.leave = leaveRes.leave;
    }
    return {
      ok: true,
      command,
      payload: {
        leaveRequestId: Number(next.leaveRequestId),
        status: next.hints?.status || next.status || "Approved",
      },
      draft: next,
    };
  }

  return { ok: false, reason: "unsupported", draft: next };
}

export async function runCommandPreview(command, payload) {
  return previewAssistantCommand({ command, payload });
}

export async function runCommandExecute(confirmToken, command) {
  return executeAssistantCommand({ confirmToken, command });
}

export async function loadCommandHistory(language) {
  const res = await fetchAssistantCommandHistory({ pageNumber: 1, pageSize: 12 });
  if (!res.success) {
    return {
      ok: false,
      message: res.message || (language === "en" ? "Could not load command history." : "Impossible de charger l'historique."),
    };
  }
  const rows = Array.isArray(res.data) ? res.data : [];
  return { ok: true, rows, stats: res.stats };
}

export function mapEstablishmentPick(row) {
  return {
    id: row.id,
    name: row.name,
    reg: row.code || "",
    detail: row.detail || "",
  };
}

export { mapTeacherRow, todayIso, cleanTeacherQuery, cleanEstablishmentQuery };
